// Backups tab — list existing snapshots + take one now.
//
// The server's runBackup snapshots the sqlite DB to a timestamped
// file under DATA_DIR; download/restore are not exposed via RPC
// today (they'd be filesystem ops on the server host). This tab
// surfaces what's there + the "snapshot now" button.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type AdminBackup } from "../../lib/rpc";

interface BackupsTabProps {
  notify: (msg: string) => void;
}

export function AdminBackupsTab({ notify }: BackupsTabProps) {
  const [backups, setBackups] = useState<AdminBackup[] | null>(null);
  const [lastAt, setLastAt] = useState<string | null>(null);
  const [dataDir, setDataDir] = useState<string>("");
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.adminBackupsList();
      setBackups(r.backups || []);
      setLastAt(r.lastBackupAt);
      setDataDir(r.dataDir);
    } catch (e: any) {
      notify(e?.message || "Couldn't list backups");
      setBackups([]);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const snapshot = async () => {
    if (running) return;
    setRunning(true);
    try {
      const r = await api.adminBackupNow();
      notify("Snapshot complete");
      setLastAt(r.lastBackupAt);
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Snapshot failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-card">
      <h3>Backups</h3>
      <p className="admin-card-sub">
        Sqlite snapshots stored alongside the data directory. Last snapshot:{" "}
        <span className="admin-user-mono">{lastAt ? lastAt.slice(0, 19).replace("T", " ") : "never"}</span>
        {dataDir && <> · <span className="admin-user-mono">{dataDir}</span></>}
      </p>

      <div className="admin-audit-filters">
        <button className="btn btn-primary" onClick={snapshot} disabled={running} type="button">
          <Icon name="database" size={13} /> {running ? "Snapshotting…" : "Snapshot now"}
        </button>
      </div>

      {backups === null ? (
        <p className="admin-loading">Loading…</p>
      ) : backups.length === 0 ? (
        <p className="admin-empty">No snapshots yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Taken</th>
            </tr>
          </thead>
          <tbody>
            {backups.map((b) => (
              <tr key={b.path}>
                <td className="admin-user-mono">{b.name}</td>
                <td>{fmtSize(b.size)}</td>
                <td className="admin-user-mono">{b.mtime ? b.mtime.slice(0, 19).replace("T", " ") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function fmtSize(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1024 * 1024 * 1024) return (n / 1024 / 1024 / 1024).toFixed(2) + " GB";
  if (n >= 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + " MB";
  if (n >= 1024) return (n / 1024).toFixed(1) + " KB";
  return `${n} B`;
}
